// === MV_PATCH: FULL FILE REPLACEMENT — per-card MV UX (uses .helper.js co-located file) ===
// Source: nucleus_dev branch (unstaged refinement)
// Pairs with weighing.controller MaterialVerification rewrite (per-card idempotent +
// completion gate). When merging back to dev, this header can be removed.
import React, { useState, useEffect } from 'react';
import Layout from '@/common/Layout';
import {
  Row,
  Col,
  Form,
  Card,
  Input,
  message,
  InputNumber,
  Space,
  Select,
  Tag,
  Checkbox,
  Tooltip,
  Spin
} from 'antd';
import styles from '@/common/Layout/index.module.less';
import FormItem from '@/common/FormItem';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import CustomButton from '@/common/CustomButton';
import CustomModal from '@/common/CustomModal';
import { QrcodeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  getAllMatrials,
  verifiedMaterial,
  downloadProdLcPdf,
  verifyQaForMaterialVerification
} from '../../../../redux/slice';
import { getAllDispenseBooths } from '@/features/dispensing/redux/slice';
import apiClient from 'utilities/apiClient';
import { history } from 'app/history';
import { QrReader } from 'react-qr-reader';
import { getLoggedInUser } from 'utilities/helpers';
import {
  buildMaterialCards,
  getGrossStatus,
  getToleranceLimits,
  formatToleranceLimit,
  isWeightMatched,
  formatBalanceName,
  combineBalanceLabel
} from './materialVerificationByProd.helper';

const materialVerificationByProd = () => {
  const { WeighingMaterials, prodDetails, getAllMaterilLoading } = useSelector(
    (state) => state.product
  );
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const params = useParams();
  const user = getLoggedInUser();

  const [openCam, setOpenCam] = useState(false);
  const [scanTargetContainer, setScanTargetContainer] = useState(null);
  const [scannedMats, setScannedMats] = useState([]);
  const [dispenseBooths, setDispenseBooths] = useState([]);
  const [weighingBal, setWeighingBal] = useState(false);
  const [balanceScanCardKey, setBalanceScanCardKey] = useState(null);
  const [qaModalOpen, setQaModalOpen] = useState(false);
  const [qaModalLoading, setQaModalLoading] = useState(false);
  const [qaForm] = Form.useForm();
  const [activeCardKey, setActiveCardKey] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [enteredWeights, setEnteredWeights] = useState({});
  const [cardBalances, setCardBalances] = useState({});
  const [cardSubmitting, setCardSubmitting] = useState({});
  const [tolerance, setTolerance] = useState(null);

  const isPacking =
    (
      prodDetails?.product_detail?.bmr_bpr_type ||
      prodDetails?.bmr_bpr_type ||
      ''
    )
      .toString()
      .toLowerCase() === 'packing';

  // Each material card (inhouse batch + lot + part) is verified independently.
  const cards = buildMaterialCards(WeighingMaterials);

  const isCardApproved = (card) =>
    card.containers.length > 0 &&
    card.containers.every((c) => c?.prod_verified && c?.qa_verified);

  const approvedCount = cards.filter(isCardApproved).length;
  const allCardsApproved = cards.length > 0 && approvedCount === cards.length;

  useEffect(() => {
    apiClient
      .get(
        `${apiClient.Urls.verificationTolerances}/`,
        { verification_type: 'material_verification' },
        true
      )
      .then((res) => {
        if (res?.success && res?.data) setTolerance(res.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    handleGetAllMaterials();
    handleGetDispenseBooths();
  }, []);

  const handleGetAllMaterials = () => {
    const payload = {
      dispensing_request_id: params.id
    };
    return dispatch(getAllMatrials(payload)).then((response) => {
      const rows = response?.payload?.data || [];
      const dispense_details = response?.payload?.dispense_details;
      const packing =
        (
          dispense_details?.product_detail?.bmr_bpr_type ||
          dispense_details?.bmr_bpr_type ||
          ''
        )
          .toString()
          .toLowerCase() === 'packing';

      // Seed entered gross weights from any persisted weighing rows so an
      // already-approved card shows what production recorded.
      const weightSeed = {};
      rows.forEach((row) => {
        const persisted = row?.weighing_gross_weights?.[0]?.prod_gross_weight;
        if (persisted !== undefined && persisted !== null && persisted !== '') {
          weightSeed[row.id] = persisted;
        }
      });
      if (Object.keys(weightSeed).length) {
        setEnteredWeights((prev) => ({ ...prev, ...weightSeed }));
      }

      // Seed each card's balance from its persisted weighing rows. Balance name
      // and dispense booth id are stored separately and re-joined for display.
      const balanceSeed = {};
      buildMaterialCards(rows).forEach((card) => {
        const persistedWeighing = card.containers
          .map((c) => c?.weighing_gross_weights?.[0])
          .find((w) => w && w.balance_name);
        if (persistedWeighing) {
          balanceSeed[card.key] = combineBalanceLabel(
            persistedWeighing.balance_name,
            persistedWeighing.dispense_booth
          );
        }
      });
      if (Object.keys(balanceSeed).length) {
        setCardBalances((prev) => ({ ...prev, ...balanceSeed }));
      }

      if (packing && user?.user?.role !== 'prod_user') {
        const preChecked = rows
          .filter((row) => !row?.qa_verified)
          .map((row) => [
            {
              isSelected: true,
              id: row?.id,
              status: 'Approved'
            }
          ]);
        setScannedMats(preChecked);
      }
      return response;
    });
  };

  const handleGetDispenseBooths = () => {
    dispatch(getAllDispenseBooths()).then((result) => {
      if (result?.payload?.success) {
        setDispenseBooths(result?.payload?.data);
      }
    });
  };

  // Records which container row the QR scan must be validated against.
  const handleQrCode = (container) => {
    setScanTargetContainer(container);
    setOpenCam(true);
  };

  const handleCardBalance = (cardKey, value) => {
    setCardBalances((prev) => ({ ...prev, [cardKey]: value }));
  };

  // Balance QR scan resolves the scanned balance name against the booth list
  // and assigns it to the card whose QR button was pressed.
  const handleQrScanWeighing = (scanData) => {
    if (scanData && scanData.text) {
      const scanned = scanData.text.trim();
      const matched = dispenseBooths?.find(
        (b) => b?.balance_name === scanned || formatBalanceName(b) === scanned
      );
      if (matched && balanceScanCardKey) {
        const balanceLabel = formatBalanceName(matched);
        handleCardBalance(balanceScanCardKey, balanceLabel);
        message.success(`Balance ${balanceLabel} selected.`);
      } else {
        message.error(`Balance "${scanned}" not found.`);
      }
      setWeighingBal(false);
      setBalanceScanCardKey(null);
    }
  };

  // Validates the scanned container QR against the row it was opened for.
  // The QR encodes `<url>?id=<container_id>&request_id=<dispensing_request_id>`.
  // The scanned container must match the row's material code, lot and dispensed
  // container number — otherwise a wrong-container error is shown.
  const handleQrScan = async (scanData) => {
    if (!scanData || scanData === '') return;
    setOpenCam(false);

    const target = scanTargetContainer;
    if (!target) {
      message.error('No container selected. Please scan from a container row.');
      return;
    }

    const qrText = scanData.text || '';
    const query = qrText.slice(qrText.lastIndexOf('?') + 1);
    const scannedParams = new URLSearchParams(query);
    const scannedContainerId = scannedParams.get('id');
    const scannedRequestId = scannedParams.get('request_id');

    if (!scannedContainerId) {
      message.error('Invalid QR code. Please scan a valid container label.');
      return;
    }

    // Reject labels that belong to a different dispensing request entirely.
    if (scannedRequestId && String(scannedRequestId) !== String(params.id)) {
      message.error(
        'Wrong container scanned — this label belongs to a different batch.'
      );
      return;
    }

    const scannedContainer = WeighingMaterials.find(
      (c) => String(c?.id) === String(scannedContainerId)
    );
    if (!scannedContainer) {
      message.error(
        'Wrong container scanned — this label is not part of this batch.'
      );
      return;
    }

    // The scanned container must be the exact one expected on this row:
    // material code + lot + dispensed container number must all match.
    const sameCode =
      (scannedContainer?.material?.code || '') ===
      (target?.material?.code || '');
    const sameLot = (scannedContainer?.lot_no || '') === (target?.lot_no || '');
    const sameDc =
      (scannedContainer?.dc_number || '') === (target?.dc_number || '');

    if (!sameCode || !sameLot || !sameDc) {
      message.error(
        `Wrong container scanned. This row expects Code ${
          target?.material?.code || 'NA'
        }, Lot ${target?.lot_no || 'NA'}, Dispensed Container ${
          target?.dc_number || 'NA'
        }.`
      );
      return;
    }

    if (isContainerSelected(target.id)) {
      message.info('This container is already verified.');
      return;
    }

    setScannedMats((prev) => [
      ...prev,
      [
        {
          dc_number: target?.dc_number,
          gross_weight: target?.gross_weight,
          id: target?.id,
          status: 'Approved'
        }
      ]
    ]);
    message.success(`Container ${target?.dc_number || ''} verified.`);
  };

  const handleCheckboxChange = (e, id) => {
    const checked = e.target.checked;
    if (checked) {
      const list = [...scannedMats];
      list.push([
        {
          isSelected: e.target.checked,
          id: id,
          status: 'Approved'
        }
      ]);
      setScannedMats(list);
    } else {
      const newData = scannedMats.flat().filter((data) => data?.id !== id);
      setScannedMats(newData);
    }
  };

  const handleError = (err) => {
    console.error(err);
  };

  const handleGrossWeight = (value, containerId) => {
    setEnteredWeights((prev) => ({
      ...prev,
      [containerId]: value
    }));
  };

  const isContainerSelected = (containerId) =>
    scannedMats.flat().some((data) => data?.id === containerId);

  // A card's Approve & Proceed is enabled only when every container in THAT
  // card is verified: scanned/checked, weight matched (non-packing) and a
  // balance selected (non-packing).
  const isCardReady = (card) => {
    if (isCardApproved(card)) return false;
    const allSelected = card.containers.every(
      (c) => c?.qa_verified || isContainerSelected(c?.id)
    );
    if (isPacking) return allSelected;
    if (!cardBalances[card.key]) return false;
    const allWeighed = card.containers.every(
      (c) =>
        c?.weighing_gross_weights?.[0]?.id ||
        isWeightMatched(c, enteredWeights, tolerance)
    );
    return allSelected && allWeighed;
  };

  const handlePrint = () => {
    const payload = {
      dispensing_request_id: params.id
    };
    setPrintLoading(true);
    return dispatch(downloadProdLcPdf(payload))
      .then((response) => {
        if (response?.payload?.success) {
          window.open(
            `${process.env.REACT_APP_STORAGE_URL}${response?.payload?.data
              .split('/')
              .pop()}`,
            '_blank'
          );
          message.success(response?.payload.message);
        } else {
          message.error(response?.payload?.message || 'Unable to generate PDF');
        }
      })
      .finally(() => setPrintLoading(false));
  };

  // Submits a single material card: only that card's unverified containers,
  // each carrying the card's balance name. When the backend reports every
  // container of the request is verified, the verification PDF is opened.
  const submitCard = (cardKey, extraPayload = {}) => {
    const card = cards.find((c) => c.key === cardKey);
    if (!card) return Promise.resolve();

    setCardSubmitting((prev) => ({ ...prev, [cardKey]: true }));
    // Split the selected combined label back into its two stored parts:
    // balance name and dispense booth id are persisted separately.
    const balanceCombo = cardBalances[cardKey] || null;
    const matchedBooth = dispenseBooths.find(
      (b) => formatBalanceName(b) === balanceCombo
    );
    const balance_name = matchedBooth
      ? matchedBooth.balance_name
      : balanceCombo;
    const dispense_booth = matchedBooth ? matchedBooth.dispense_booth_id : null;
    const enriched = card.containers
      .filter((c) => !(c?.prod_verified && c?.qa_verified))
      .map((c) => ({
        id: c?.id,
        status: 'Approved',
        prod_gross_weight: enteredWeights[c?.id],
        balance_name,
        dispense_booth,
        tolerance_id: tolerance?.id || null
      }));

    const payload = {
      dispensing_request_id: params.id,
      material_verified_status: enriched,
      ...extraPayload
    };

    return dispatch(verifiedMaterial(payload))
      .then(async (response) => {
        if (response?.payload?.success) {
          message.success(response?.payload.message);
          await handleGetAllMaterials();
          if (response?.payload?.all_verified) {
            await handlePrint();
          }
        } else {
          message.error(
            response?.payload?.message || 'Material verification failed'
          );
        }
        return response;
      })
      .catch((err) => {
        message.error(`Submission failed: ${err?.message || 'unknown error'}`);
      })
      .finally(() => {
        setCardSubmitting((prev) => ({ ...prev, [cardKey]: false }));
      });
  };

  const handleSubmit = (cardKey) => {
    setActiveCardKey(cardKey);
    setQaModalOpen(true);
  };

  // The QA modal collects the QA approver's credentials for one card. After
  // they are validated, the card is submitted stamping both prod and QA.
  const handleQaVerifyAndSubmit = (values) => {
    setQaModalLoading(true);
    const verifyPayload = {
      employee_id: values.employee_id,
      password: values.password,
      comments: values.comments,
      dispensing_request_id: params.id
    };
    dispatch(verifyQaForMaterialVerification(verifyPayload))
      .then((response) => {
        if (response?.payload?.success) {
          message.success(response?.payload.message || 'QA verified');
          const cardKey = activeCardKey;
          setQaModalOpen(false);
          qaForm.resetFields();
          return submitCard(cardKey, {
            qa_verifier_employee_id: values.employee_id,
            qa_verifier_comments: values.comments
          });
        }
        message.error(response?.payload?.message || 'QA verification failed');
        return null;
      })
      .finally(() => setQaModalLoading(false));
  };

  const handleQaModalClose = () => {
    setQaModalOpen(false);
    setActiveCardKey(null);
    qaForm.resetFields();
  };

  const activeCard = cards.find((c) => c.key === activeCardKey);

  return (
    <Layout sider={false}>
      <Spin spinning={getAllMaterilLoading && WeighingMaterials.length === 0}>
        <Form form={form} layout="vertical">
          <Card
            title={
              <span style={{ fontWeight: 'bold' }}>Material Verification</span>
            }
            className={styles.CustomPanel}
            extra={
              <Space size={8}>
                {cards.length > 0 && (
                  <Tag
                    bordered={false}
                    color={allCardsApproved ? '#87d068' : '#108ee9'}
                  >
                    {approvedCount}/{cards.length} Verified
                  </Tag>
                )}
                {/* Print is available once ANY card is approved so a partial
                    verification PDF can be pulled without waiting for every
                    card. The PDF renders verified cards stamped and leaves
                    unverified ones blank. */}
                {approvedCount > 0 && (
                  <CustomButton
                    className={styles.inwardButton}
                    onClick={handlePrint}
                    type="primary"
                    size="small"
                    loading={printLoading}
                    disabled={printLoading}
                  >
                    Print
                  </CustomButton>
                )}
              </Space>
            }
          >
            <Card className={styles.subpanel}>
              <Row gutter={24}>
                <Col span={12}>
                  <FormItem label="Product Name" className={styles.customLable}>
                    <Input
                      disabled
                      value={prodDetails?.product_detail?.name || 'NA'}
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem
                    label="Product Batch Number"
                    className={styles.customLable}
                  >
                    <Input
                      disabled
                      value={prodDetails?.product_batch_number || 'NA'}
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem
                    label="Effective Date"
                    className={styles.customLable}
                  >
                    <Input
                      disabled
                      value={
                        prodDetails?.effective_date
                          ? moment(prodDetails?.effective_date).format(
                              'DD MMM YYYY'
                            )
                          : 'NA'
                      }
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem label="Batch Size" className={styles.customLable}>
                    <Input
                      disabled
                      value={
                        prodDetails?.product_detail?.theoretical_batch_size ||
                        prodDetails?.product_detail?.batch_size ||
                        'NA'
                      }
                    />
                  </FormItem>
                </Col>
                {/* Equipment Name field not required on this screen.
                {!isPacking && (
                  <Col span={12}>
                    <FormItem
                      label="Equipment Name"
                      className={styles.customLable}
                    >
                      <Input
                        disabled
                        value={
                          prodDetails?.product_detail?.equipment_name || 'NA'
                        }
                      />
                    </FormItem>
                  </Col>
                )}
                */}
              </Row>
            </Card>
            <br />

            {cards.map((card) => {
              const first = card.containers?.[0];
              const lot_no = first?.lot_no ? ` - ${first.lot_no}` : '';
              const part_no = first?.part_no ? ` - ${first.part_no}` : '';
              const cardApproved = isCardApproved(card);
              return (
                <React.Fragment key={card.key}>
                  <Card
                    className={styles.CustomPanel}
                    title={`Material${
                      first?.material?.bmr_product_materials?.[0]
                        ?.dispensing_sequence ?? ''
                    }${lot_no}${part_no}`}
                    extra={
                      cardApproved ? (
                        <Tag bordered={false} color="#87d068">
                          Approved
                        </Tag>
                      ) : (
                        <CustomButton
                          type="primary"
                          size="small"
                          className={styles.inwardButton}
                          loading={!!cardSubmitting[card.key]}
                          disabled={
                            !isCardReady(card) || !!cardSubmitting[card.key]
                          }
                          onClick={() => handleSubmit(card.key)}
                        >
                          Approve & Proceed
                        </CustomButton>
                      )
                    }
                  >
                    {!isPacking && (
                      <Row gutter={24}>
                        <Col
                          xs={4}
                          xl={1}
                          span={2}
                          md={2}
                          sm={2}
                          style={{ paddingLeft: '0px !important' }}
                        >
                          <CustomButton
                            className={styles.qrcode}
                            style={{ marginTop: '24px' }}
                            disabled={cardApproved}
                          >
                            <QrcodeOutlined
                              onClick={() => {
                                if (cardApproved) return;
                                setBalanceScanCardKey(card.key);
                                setWeighingBal(true);
                              }}
                            />
                          </CustomButton>
                        </Col>
                        <Col xs={24} xl={11} span={12}>
                          <FormItem
                            label="Balance Name"
                            className={styles.customLable}
                          >
                            <Select
                              placeholder="Select Balance Name"
                              onChange={(value) =>
                                handleCardBalance(card.key, value)
                              }
                              value={cardBalances[card.key]}
                              disabled={cardApproved}
                            >
                              {dispenseBooths
                                ?.filter((item) => {
                                  return item['bal_type'] === 'dispense_prod';
                                })
                                ?.map((item, index) => {
                                  const balanceLabel = formatBalanceName(item);
                                  return (
                                    <Select.Option
                                      value={balanceLabel}
                                      key={index}
                                    >
                                      {balanceLabel}
                                    </Select.Option>
                                  );
                                })}
                            </Select>
                          </FormItem>
                        </Col>
                      </Row>
                    )}

                    <Row gutter={24}>
                      <Col xs={24} xl={8} span={24} md={24} sm={24}>
                        <FormItem label="Name" className={styles.customLable}>
                          <Input
                            placeholder="Enter Material Name"
                            value={first?.material?.name || ''}
                            disabled
                          />
                        </FormItem>
                      </Col>
                      <Col xs={24} xl={4} span={24} md={24} sm={24}>
                        <FormItem label="Code" className={styles.customLable}>
                          <Input
                            placeholder="Enter Material Code"
                            value={first?.material?.code || ''}
                            disabled
                          />
                        </FormItem>
                      </Col>
                      <Col xs={24} xl={6} span={24} md={24} sm={24}>
                        <FormItem
                          label="Inhouse Batch #"
                          className={styles.customLable}
                        >
                          <Input
                            placeholder="Enter Inhouse Batch Number"
                            value={
                              first?.batch_detail?.inhouse_batch_number || ''
                            }
                            disabled
                          />
                        </FormItem>
                      </Col>
                      <Col xs={24} xl={4} span={24} md={24} sm={24}>
                        <FormItem
                          label="Gross Weight"
                          className={styles.customLable}
                        >
                          <Input
                            placeholder="Enter Gross Weight"
                            value={first?.gross_weight || ''}
                            disabled
                          />
                        </FormItem>
                      </Col>
                    </Row>

                    {card.containers.map((item1, index1) => {
                      return (
                        <Row gutter={24} key={index1}>
                          <Col xs={24} xl={4} span={24} md={24} sm={24}>
                            <FormItem
                              label={<span>Dispensed container</span>}
                              className={styles.customLable}
                            >
                              <Space>
                                {user?.user?.role == 'qc_initiator' && (
                                  <Checkbox
                                    checked={
                                      scannedMats
                                        ?.flat()
                                        .filter(
                                          (data) => data?.id === item1?.id
                                        ).length || item1?.qa_verified
                                    }
                                    onChange={(e) =>
                                      handleCheckboxChange(e, item1?.id)
                                    }
                                    disabled={
                                      scannedMats
                                        ?.flat()
                                        .filter(
                                          (data) => data?.id === item1?.id
                                        ).length ||
                                      item1?.qa_verified ||
                                      cardApproved
                                    }
                                  />
                                )}
                                <Input
                                  style={{ borderRadius: '12px' }}
                                  placeholder="Enter Material Name"
                                  value={item1?.dc_number}
                                  disabled
                                />
                              </Space>
                            </FormItem>
                          </Col>
                          <Col xs={24} xl={4} span={24} md={24} sm={24}>
                            <FormItem
                              label="Gross Weight"
                              className={styles.customLable}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Gross Weight"
                                value={item1?.gross_weight}
                                disabled
                              />
                            </FormItem>
                          </Col>
                          <Col xs={24} xl={1} span={24} md={24} sm={24}>
                            <FormItem label="" className={styles.customLable}>
                              <CustomButton
                                style={{ marginTop: '24px' }}
                                disabled={
                                  scannedMats
                                    ?.flat()
                                    .filter((data) => data?.id === item1?.id)
                                    .length ||
                                  item1?.weighing_gross_weights?.[0]?.id ||
                                  cardApproved
                                }
                              >
                                <QrcodeOutlined
                                  onClick={() => handleQrCode(item1)}
                                />
                              </CustomButton>
                            </FormItem>
                          </Col>
                          <Col
                            xs={24}
                            xl={9}
                            span={24}
                            md={24}
                            sm={24}
                            style={{ marginLeft: '5px' }}
                          >
                            <FormItem
                              label="Container to verify"
                              className={styles.customLable}
                            >
                              <Input
                                placeholder="Scan Container"
                                value={
                                  scannedMats
                                    ?.flat()
                                    .filter((data) => data?.id === item1?.id)[0]
                                    ?.dc_number ||
                                  item1?.weighing_gross_weights?.[0]?.id
                                    ? item1?.dc_number
                                    : ''
                                }
                                disabled
                              />
                            </FormItem>
                          </Col>
                          {isPacking && (
                            <Col
                              xs={24}
                              xl={10}
                              span={24}
                              md={24}
                              sm={24}
                              style={{ marginLeft: '5px' }}
                            >
                              <FormItem
                                label={
                                  <Space>
                                    Container verified{' '}
                                    {item1?.prod_verified && (
                                      <Tag
                                        bordered={false}
                                        color="#87d068"
                                        style={{ fontSize: '9px' }}
                                      >
                                        Verified by Prod
                                      </Tag>
                                    )}
                                    {item1?.qa_verified && (
                                      <Tag
                                        bordered={false}
                                        color="#87d068"
                                        style={{ fontSize: '9px' }}
                                      >
                                        Verified by QA
                                      </Tag>
                                    )}
                                  </Space>
                                }
                                className={styles.customLable}
                              >
                                <Checkbox
                                  checked={
                                    !!scannedMats
                                      ?.flat()
                                      .filter((data) => data?.id === item1?.id)
                                      .length || !!item1?.qa_verified
                                  }
                                  onChange={(e) =>
                                    handleCheckboxChange(e, item1?.id)
                                  }
                                  disabled={
                                    !!item1?.qa_verified || cardApproved
                                  }
                                >
                                  Container verified
                                </Checkbox>
                              </FormItem>
                            </Col>
                          )}
                          {!isPacking && (
                            <Col xs={24} xl={4} span={24} md={24} sm={24}>
                              <FormItem
                                label={
                                  <Space size={4}>
                                    <span>Gross Weight</span>
                                    {tolerance && (
                                      <Tooltip
                                        title={(() => {
                                          const limits = getToleranceLimits(
                                            item1?.gross_weight,
                                            tolerance
                                          );
                                          const band = limits
                                            ? ` of ${formatToleranceLimit(
                                                limits.reference
                                              )} — allowed ${formatToleranceLimit(
                                                limits.lower
                                              )} to ${formatToleranceLimit(
                                                limits.upper
                                              )}`
                                            : '';
                                          return `Tolerance: ±${
                                            tolerance.tolerance_percentage
                                          }%${band} (${
                                            tolerance.description ||
                                            tolerance.verification_type
                                          })`;
                                        })()}
                                      >
                                        <InfoCircleOutlined
                                          style={{ color: '#c91761' }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Space>
                                }
                                className={styles.customLable}
                              >
                                <InputNumber
                                  style={{ width: '100%' }}
                                  placeholder="Gross Weight"
                                  value={enteredWeights[item1?.id]}
                                  disabled={
                                    !!item1?.weighing_gross_weights?.[0]
                                      ?.prod_gross_weight || cardApproved
                                  }
                                  onChange={(value) =>
                                    handleGrossWeight(value, item1?.id)
                                  }
                                />
                                <CustomButton
                                  style={{
                                    color: '#c91761',
                                    marginTop: '2px'
                                  }}
                                  size="small"
                                  disabled={
                                    !!item1?.weighing_gross_weights?.[0]
                                      ?.prod_gross_weight || cardApproved
                                  }
                                >
                                  Read
                                </CustomButton>
                                {(() => {
                                  const status = getGrossStatus(
                                    item1,
                                    enteredWeights,
                                    tolerance
                                  );
                                  if (status === 'match')
                                    return (
                                      <Tag
                                        color="green"
                                        style={{ marginTop: '4px' }}
                                      >
                                        Match
                                      </Tag>
                                    );
                                  if (status === 'within')
                                    return (
                                      <Tag
                                        color="gold"
                                        style={{ marginTop: '4px' }}
                                      >
                                        Within tolerance
                                      </Tag>
                                    );
                                  if (status === 'beyond')
                                    return (
                                      <Tag
                                        color="red"
                                        style={{ marginTop: '4px' }}
                                      >
                                        Beyond tolerance
                                      </Tag>
                                    );
                                  return null;
                                })()}
                              </FormItem>
                            </Col>
                          )}
                        </Row>
                      );
                    })}
                  </Card>
                  <br />
                </React.Fragment>
              );
            })}

            {openCam && (
              <CustomModal
                title="Scan Container QR"
                visible={openCam}
                onCancel={() => setOpenCam(false)}
                footer={null}
                customFooter={false}
                width="400px"
                destroyOnClose
              >
                <div style={{ width: '100%' }}>
                  <QrReader
                    constraints={{ facingMode: 'environment' }}
                    delay={1000}
                    onError={handleError}
                    onResult={(result) => handleQrScan(result)}
                    containerStyle={{ width: '100%' }}
                  />
                  <p style={{ textAlign: 'center', marginTop: '12px' }}>
                    Point camera at the container QR code
                  </p>
                </div>
              </CustomModal>
            )}

            {weighingBal && (
              <CustomModal
                title="Scan Balance QR"
                visible={weighingBal}
                onCancel={() => {
                  setWeighingBal(false);
                  setBalanceScanCardKey(null);
                }}
                footer={null}
                customFooter={false}
                width="400px"
                destroyOnClose
              >
                <div style={{ width: '100%' }}>
                  <QrReader
                    constraints={{ facingMode: 'environment' }}
                    delay={1000}
                    onError={handleError}
                    onResult={(result) => handleQrScanWeighing(result)}
                    containerStyle={{ width: '100%' }}
                  />
                  <p style={{ textAlign: 'center', marginTop: '12px' }}>
                    Point camera at the balance QR code
                  </p>
                </div>
              </CustomModal>
            )}
          </Card>
          <br />
          <FormItem className="text-center">
            <CustomButton
              className={styles.inwardButton}
              onClick={() => history.push('/qa-lc-list')}
              type="primary"
              ghost
            >
              Back to List
            </CustomButton>
          </FormItem>
        </Form>
      </Spin>
      <CustomModal
        title={`Verification By QA${
          activeCard?.containers?.[0]?.material?.name
            ? ` — ${activeCard.containers[0].material.name}`
            : ''
        }`}
        visible={qaModalOpen}
        width="50%"
        footer={null}
        className={styles.customModal}
        closable={false}
      >
        <Form
          form={qaForm}
          layout="vertical"
          onFinish={handleQaVerifyAndSubmit}
        >
          <Row gutter={24}>
            <Col span={12}>
              <FormItem
                label="User ID"
                name="employee_id"
                rules={[
                  {
                    required: true,
                    message: 'Enter User ID'
                  }
                ]}
                className={styles.customLable}
              >
                <Input placeholder="Enter User ID" />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                label="Password"
                name="password"
                rules={[
                  {
                    required: true,
                    message: 'Enter Password'
                  }
                ]}
                className={styles.customLable}
              >
                <Input.Password placeholder="Enter Password" />
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem
                label="Comments"
                name="comments"
                rules={[
                  {
                    required: true,
                    message: 'Enter Comments'
                  }
                ]}
                className={styles.customLable}
              >
                <Input.TextArea placeholder="Enter Comments" />
              </FormItem>
            </Col>
          </Row>
          <FormItem className="text-center">
            <Space>
              <CustomButton
                className={styles.inwardButton}
                onClick={handleQaModalClose}
                type="primary"
                size="small"
                ghost
                disabled={qaModalLoading}
              >
                Close
              </CustomButton>
              <CustomButton
                className={styles.inwardButton}
                htmlType="submit"
                type="primary"
                size="small"
                loading={qaModalLoading}
                disabled={qaModalLoading}
              >
                Verify
              </CustomButton>
            </Space>
          </FormItem>
        </Form>
      </CustomModal>
    </Layout>
  );
};
export default materialVerificationByProd;
